// LandingAnimatedPaginator.tsx - reusable animated paginator component for landing pages
// No explicit React import needed with the new JSX transform

/**
 * Reusable animated paginator that mimics the parallelogram meter styling
 * used across the landing page (e.g., Bracky section, advanced features carousel).
 *
 * Props:
 *  - total:     total number of items / pages (default 4)
 *  - active:    the currently active index (0-based)
 *  - onDotClick optional click handler for interactive use (omit for static display)
 *  - className: optional additional wrapper classes
 */
type LandingAnimatedPaginatorProps = {
  total?: number
  active: number
  onDotClick?: (index: number) => void
  className?: string
}

export function LandingAnimatedPaginator({
  total = 4,
  active,
  onDotClick,
  className = "",
}: LandingAnimatedPaginatorProps) {
  return (
    <div className={`flex justify-center items-center space-x-[.125rem] ${className}`.trim()}>
      {Array.from({ length: total }).map((_, index) => {
        const isActive = index === active
        const isCompleted = index < active

        // Base parallelogram shape shared by every meter
        const baseClasses = "h-[0.45rem] sm:h-2 transform skew-x-[-16deg] rounded-[1px] transition-all duration-300"

        // Width – active is 3× wider
        const widthClasses = isActive ? "w-[1.45rem]" : "w-2"

        // Color / outline rules
        const stateClasses = isActive
          ? "bg-[#22FF65] animate-pulse" // breathing green for current
          : // ? "bg-[#30DDFF] animate-pulse" //
            isCompleted
            ? "bg-[#0074FF]" // blue for completed
            : "bg-white/15" // upcoming

        return (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: Paginator dots are inherently indexed - the index IS the meaningful identifier
            key={`page-${index}`}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => onDotClick?.(index)}
            className={`${baseClasses} ${widthClasses} ${stateClasses}`}
            style={{
              flex: "0 0 auto",
              animationDuration: isActive ? "1.6s" : undefined, // match pulse speed from Bracky section
              boxShadow: isActive
                ? "0 0 10px 3px rgba(34, 255, 101, 0), 0 0 15px 5px rgba(34, 255, 101, 0.33)"
                : undefined,
            }}
          />
        )
      })}
    </div>
  )
}
